/**
 * Toolbar for the questionnaire body: page title, patient badges, CMM key,
 * MN judge preview badges, and action buttons (Prediction Summary +
 * Manage Attachments).
 *
 * @module pharmaQuestionaire/components/QuestionnaireToolbar
 */
import { Button } from "risa-oasis-ui_v2";
import { EvaluationDecision } from "../../../components/modals/predictionSummaryModal/types";
import { NycbsPharmaOrderModel } from "../../../data-model/nycbsPharmaOrder";
import { NycbsPharmaOrderKeys } from "../../../enums/nycbsPharmaOrder";
import CmmKeyDisplay from "./CmmKeyDisplay";
import MnJudgePreviewBadges from "./MnJudgePreviewBadges";
import PatientInfoBadges from "./PatientInfoBadges";

/**
 * Props for the questionnaire page toolbar.
 */
interface QuestionnaireToolbarProps {
  /** Current CMM order used for patient badges and CMM key display. */
  singleCmmOrderData: NycbsPharmaOrderModel | undefined;
  /** Opens the manage-attachments / document merge modal. */
  onManageAttachments: () => void;
  /** Opens the Prediction Summary (MN judge) modal. */
  onPredictionSummary: () => void;
  /** Latest MN judge decision to show before Prediction Summary, if any. */
  mnJudgeDecision?: EvaluationDecision | null;
  /** Latest MN judge confidence percent to show before Prediction Summary. */
  mnJudgeConfidence?: number | null;
}

/**
 * Renders the questionnaire body header toolbar.
 *
 * @param props - Toolbar data and action handlers
 * @returns Toolbar UI with title, badges, and action buttons
 */
const QuestionnaireToolbar = ({
  singleCmmOrderData,
  onManageAttachments,
  onPredictionSummary,
  mnJudgeDecision = null,
  mnJudgeConfidence = null,
}: QuestionnaireToolbarProps) => {
  return (
    <div className="pharma-questionaire-form__body--header mb-4">
      <div className="pharma-questionaire-form__body--header-details flex items-center justify-between gap-2">
        <div className="pharma-questionaire-form__body--header titles flex gap-2">
          <div className="pharma-questionaire-form__body-page-name flex text-large font-semiBold leading-6 text-black">
            Attachments and Questionnaire
          </div>
          <PatientInfoBadges singleCmmOrderData={singleCmmOrderData} />
        </div>
        <CmmKeyDisplay
          cmmKey={singleCmmOrderData?.[NycbsPharmaOrderKeys.CmmResultKey] ?? ""}
        />
        <div className="procedure-pa-form__attachements flex items-center gap-2">
          {mnJudgeDecision != null && mnJudgeConfidence != null && (
            <MnJudgePreviewBadges
              decision={mnJudgeDecision}
              confidence={mnJudgeConfidence}
            />
          )}
          <Button
            disabled={false}
            children={"Prediction Summary"}
            onClick={onPredictionSummary}
            buttonType={"secondary"}
            size={"medium"}
          />
          <Button
            disabled={false}
            children={"Manage Attachments"}
            onClick={onManageAttachments}
            buttonType={"secondary"}
            size={"medium"}
          />
        </div>
      </div>
    </div>
  );
};

export default QuestionnaireToolbar;
